// @ts-check
import { EXTENSION_CONFIG } from "../config/constants.js";
import { Logger } from "../utils/Logger.js";
import { MarkerManager } from "../services/MarkerManager.js";
import { EventHandler } from "../services/EventHandler.js";
import { MarkupApiPersistenceService } from "../services/MarkupApiPersistenceService.js";

/** @type {any} */
const Autodesk = /** @type {any} */ (window).Autodesk;

/**
 * Refactored Custom Markup Extension for APS Viewer
 *
 * This is the main extension class that orchestrates all the services and components.
 * It follows the separation of concerns principle with focused responsibilities.
 */
export class CustomMarkupExtension extends Autodesk.Viewing.Extension {
	constructor(viewer, options) {
		super(viewer, options);

		/** @type {any} */
		this.viewer = viewer; // Explicitly store viewer reference for @ts-check

		this.logger = Logger.createLogger("CustomMarkupExtension");

		// Initialize state
		this.isMarkupMode = false;
		this.currentViewType = options?.viewType || "3D"; // Default to 3D view
		this.markupSymbol = "M"; // Default cursor symbol for markup mode
		this.markupImage = null; // Placeholder for custom markup image if needed
		this.viewerUrn = options?.viewerUrn || null;
		this.currentViewIndex = options?.viewIndex || -1; // -1 for 3D, 0+ for 2D sheet index

		// Toolbar UI elements
		this._group = null;
		this._button = null;

		// Store user data from options
		this.userImageLink = options?.userImageLink || null;
		this.userName = options?.userName || "";
		this.buildingDisciplineFileId = options?.buildingDisciplineFileId || null;
		this.apiActions = options?.apiActions || null;

		// Initialize services and pass user data
		this.markerManager = new MarkerManager(viewer, {
			userImageLink: this.userImageLink,
			userName: this.userName,
			mainExtension: this, // Pass reference to this extension
			fileId: this.buildingDisciplineFileId,
		});
		this.eventHandler = new EventHandler(viewer);

		// Initialize persistence service based on available options
		if (this.buildingDisciplineFileId && this.apiActions) {
			this.persistenceService = new MarkupApiPersistenceService(
				this.buildingDisciplineFileId,
				this.apiActions
			);
		} else {
			this.logger.warn(
				"No buildingDisciplineFileId or apiActions provided, persistence disabled"
			);
			this.persistenceService = null;
		}

		// Bind methods
		this.onObjectClick = this.onObjectClick.bind(this);
		this.onCameraChange = this.onCameraChange.bind(this);
		this.onViewerClick = this.onViewerClick.bind(this);
	}

	/**
	 * Extension load callback
	 * @returns {boolean} True if loaded successfully
	 */
	load() {
		try {
			// Initialize event handlers
			this.eventHandler.initialize();

			// Set up event listeners
			this.setupEventListeners();

			// Load and enable Markups Core extension
			this.loadMarkupsCoreExtension();

			// load markup persistence service
			this.loadMarkups();

			// Enable markup mode by default
			this.enableMarkupMode();

			return true;
		} catch (error) {
			this.logger.error("Error loading Custom Markup Extension", error);
			return false;
		}
	}

	/**
	 * Extension unload callback
	 * @returns {boolean} True if unloaded successfully
	 */
	unload() {
		try {
			// Disable markup mode
			this.disableMarkupMode();

			// Clean up toolbar UI elements
			if (
				this._group &&
				this._button &&
				this._group instanceof Autodesk.Viewing.UI.ControlGroup
			) {
				this._group.removeControl(this._button);
				if (this._group.getNumberOfControls() === 0) {
					this.viewer.toolbar.removeControl(this._group);
				}
			}

			// Remove viewer click listener
			this.viewer.container.removeEventListener("click", this.onViewerClick);

			// Clear all markers
			this.markerManager.clearAllMarkers();

			// Clean up event handlers
			this.eventHandler.destroy();

			return true;
		} catch (error) {
			this.logger.error("Error unloading Custom Markup Extension", error);
			return false;
		}
	}

	/**
	 * Set up event listeners
	 */
	setupEventListeners() {
		// Object selection events
		this.eventHandler.onSelectionChanged(this.onObjectClick);

		// Camera change events (with appropriate throttling based on view type)
		this.eventHandler.onCameraChanged(this.onCameraChange, this.is2DView());

		// Click on empty space to deactivate markers
		this.viewer.container.addEventListener(
			"click",
			this.onViewerClick.bind(this)
		);
	}

	/**
	 * Handle object click events
	 * @param {Object} event - Selection change event
	 */
	onObjectClick(event) {
		if (
			!this.isMarkupMode ||
			!event.dbIdArray ||
			event.dbIdArray.length === 0
		) {
			return;
		}

		const dbId = event.dbIdArray[0];

		if (!this.is2DView()) {
			// Create marker on the clicked object
			this.createMarkerOnObject(dbId);
		} else {
			// For 2D views, create a marker at the clicked position
			this.onViewerClick(event);
		}

		// Clear selection after creating marker
		setTimeout(() => {
			this.viewer.clearSelection();
		}, 100);
	}

	/**
	 * Handle clicks on empty space in the viewer (not on objects or markers)
	 * @param {MouseEvent} event - Click event
	 */
	async onViewerClick(event) {
		// Only handle in markup mode for 2D views
		if (!this.isMarkupMode || !this.is2DView()) return;

		// Skip if clicking on a marker
		const target = event.target;
		const isMarkerClick =
			target instanceof HTMLElement &&
			(target.classList.contains("custom-marker") ||
				target.closest(".custom-marker"));
		if (isMarkerClick) return;

		event.stopPropagation();

		// Get screen coordinates relative to viewer container
		const rect = this.viewer.container.getBoundingClientRect();
		const screenX = event.clientX - rect.left;
		const screenY = event.clientY - rect.top;

		// Convert screen coordinates to model coordinates for 2D views
		let modelPoint = null;

		try {
			// Use the viewer's clientToWorld method for proper coordinate conversion
			const worldPoint = this.viewer.clientToWorld(screenX, screenY);
			if (worldPoint) {
				modelPoint = {
					x: worldPoint.point.x,
					y: worldPoint.point.y,
					z: worldPoint.point.z || 0,
				};
			}
		} catch (error) {
			this.logger.warn("Failed to convert screen to world coordinates:", error);
		}

		// Fallback: if conversion failed, use screen coordinates with appropriate scaling
		if (!modelPoint) {
			// For 2D views, screen coordinates might need to be used directly
			// but we should use the actual click position
			modelPoint = { x: screenX, y: screenY, z: 0 };
		}

		// Create marker at the converted model position
		try {
			await this.markerManager.createMarkerAt2DPoint(
				this.viewer.model,
				modelPoint
			);

			// Auto-save markups after creation if we have a valid URN
			if (this.viewerUrn) {
				this.saveMarkups().catch((err) => {
					this.logger.warn("Auto-save failed after marker creation", err);
				});
			}
			this.logger.debug(
				`Created 2D marker at model coordinates (${modelPoint.x}, ${modelPoint.y})`
			);
		} catch (error) {
			this.logger.error("Failed to create 2D marker:", error);
		}
	}

	/**
	 * Handle camera change events
	 */
	onCameraChange() {
		if (this.markerManager.getMarkerCount() === 0) {
			return;
		}

		this.markerManager.updateAllMarkerPositions();
	}

	/**
	 * Create a marker on an object
	 * @param {number} dbId - Database ID of the object
	 * @returns {Promise<Object|null>} Created marker or null
	 */
	async createMarkerOnObject(dbId) {
		try {
			const marker = await this.markerManager.createMarker(dbId);

			if (marker) {
				// Auto-save markups after creation if we have a valid URN
				if (this.viewerUrn) {
					this.saveMarkups().catch((err) => {
						this.logger.warn("Auto-save failed after marker creation", err);
					});
				}
			} else {
				this.logger.warn(`Failed to create marker on object ${dbId}`);
			}

			return marker;
		} catch (error) {
			this.logger.error(`Error creating marker on object ${dbId}`, error);
			return null;
		}
	}

	/**
	 * Enable markup mode
	 */
	enableMarkupMode() {
		this.isMarkupMode = true;
		this.viewer.clearSelection();
		// add cursor class
		if (this.viewer?.container) {
			this.viewer.container.classList.add("custom-markup-mode");
		}
		this.updateButtonState();
	}

	/**
	 * Disable markup mode
	 */
	disableMarkupMode() {
		this.isMarkupMode = false;
		if (this.viewer?.container) {
			this.viewer.container.classList.remove("custom-markup-mode");
		}
		this.updateButtonState();
	}

	/**
	 * Toggle markup mode
	 */
	toggleMarkupMode() {
		if (this.isMarkupMode) {
			this.disableMarkupMode();
		} else {
			this.enableMarkupMode();
		}

		// Update button state
		this.updateButtonState();
	}

	/**
	 * Called when the toolbar is created
	 */
	onToolbarCreated() {
		// Create a new toolbar group if it doesn't exist
		this._group = this.viewer.toolbar.getControl("customMarkupToolbar");
		if (
			!this._group ||
			!(this._group instanceof Autodesk.Viewing.UI.ControlGroup)
		) {
			this._group = new Autodesk.Viewing.UI.ControlGroup("customMarkupToolbar");
			this.viewer.toolbar.addControl(this._group);
		}

		// Add a new button to the toolbar group
		this._button = new Autodesk.Viewing.UI.Button("customMarkupButton");
		this._button.onClick = (ev) => {
			this.toggleMarkupMode();
		};
		this._button.setToolTip("Toggle Markup Mode");
		this._button.addClass("custom-markup-icon");

		// Ensure we have a valid control group before adding the button
		if (this._group instanceof Autodesk.Viewing.UI.ControlGroup) {
			this._group.addControl(this._button);
		}

		// Set initial button state
		this.updateButtonState();
	}

	/**
	 * Update button state based on markup mode
	 */
	updateButtonState() {
		if (this._button) {
			if (this.isMarkupMode) {
				this._button.setState(Autodesk.Viewing.UI.Button.State.ACTIVE);
				this._button.addClass("active");
			} else {
				this._button.setState(Autodesk.Viewing.UI.Button.State.INACTIVE);
				this._button.removeClass("active");
			}
		}
	}

	/**
	 * Set view type and update positioning strategy
	 * @param {string} viewType - '2D' or '3D'
	 * @param {number} viewIndex - View index (-1 for 3D, 0+ for 2D sheets)
	 */
	setViewType(viewType, viewIndex = -1) {
		const viewChanged =
			this.currentViewType !== viewType || this.currentViewIndex !== viewIndex;

		if (viewChanged) {
			this.currentViewType = viewType;
			this.currentViewIndex = viewIndex;

			// Clear persistence tracking when switching views since each view has its own persisted markers
			if (
				this.persistenceService &&
				typeof this.persistenceService.clearPersistedMarkerTracking ===
					"function"
			) {
				this.persistenceService.clearPersistedMarkerTracking();
			}

			// Update positioning strategy
			this.markerManager.updatePositioningStrategy(viewType);

			// Auto-load saved markups for this view
			if (this.viewerUrn) {
				this.loadMarkups().catch((error) => {
					this.logger.error("Failed to auto-load markups", error);
				});
			}
		}
	}

	/**
	 * Update view type and index when the view changes
	 * @param {string} viewType - '2D' or '3D'
	 * @param {number} viewIndex - View index (for 2D sheets)
	 */
	updateViewInfo(viewType, viewIndex) {
		this.currentViewType = viewType;
		this.currentViewIndex = viewIndex;

		// Update positioning strategy for marker manager
		if (this.markerManager) {
			this.markerManager.updatePositioningStrategy(viewType);
		}

		// Clear existing markers and reload for the new view
		this.markerManager.clearAllMarkers();
		this.loadMarkups();
	}

	/**
	 * Check if current view is 2D
	 * @returns {boolean} True if 2D view
	 */
	is2DView() {
		return this.currentViewType === "2D" || this.viewer.model?.is2d();
	}

	/**
	 * Remove a specific marker
	 * @param {number} markerId - Marker ID to remove
	 * @returns {boolean} True if removed successfully
	 */
	removeMarker(markerId) {
		return this.markerManager.removeMarker(markerId);
	}

	/**
	 * Clear all markers
	 */
	clearAllMarkers() {
		this.markerManager.clearAllMarkers();

		// Clear persistence tracking to allow fresh start
		if (
			this.persistenceService &&
			typeof this.persistenceService.clearPersistedMarkerTracking === "function"
		) {
			this.persistenceService.clearPersistedMarkerTracking();
		}

		// If there's a valid URN, also delete saved markups
		if (this.viewerUrn) {
			this.deleteMarkups().catch((err) => {
				this.logger.warn(
					"Failed to delete saved markups during clear operation",
					err
				);
			});
		}
	}

	/**
	 * Get all markers
	 * @returns {Array} Array of marker data
	 */
	getMarkers() {
		return this.markerManager.getAllMarkers().map((marker) => ({
			id: marker.id,
			dbId: marker.dbId,
			timestamp: marker.timestamp,
		}));
	}

	/**
	 * Get marker count
	 * @returns {number} Number of markers
	 */
	getMarkerCount() {
		return this.markerManager.getMarkerCount();
	}

	/**
	 * Set a marker as active (green state)
	 * @param {number} markerId - Marker ID to set as active
	 * @returns {boolean} True if successful
	 */
	setActiveMarker(markerId) {
		return this.markerManager.setActiveMarker(markerId);
	}

	/**
	 * Deactivate a specific marker
	 * @param {number} markerId - Marker ID to deactivate
	 * @returns {boolean} True if successful
	 */
	deactivateMarker(markerId) {
		return this.markerManager.deactivateMarker(markerId);
	}

	/**
	 * Deactivate all markers
	 */
	deactivateAllMarkers() {
		this.markerManager.deactivateAllMarkers();
	}

	/**
	 * Get currently active marker ID
	 * @returns {number|null} Active marker ID or null
	 */
	getActiveMarkerId() {
		return this.markerManager.getActiveMarkerId();
	}

	/**
	 * Check if a marker is active
	 * @param {number} markerId - Marker ID to check
	 * @returns {boolean} True if marker is active
	 */
	isMarkerActive(markerId) {
		return this.markerManager.isMarkerActive(markerId);
	}

	/**
	 * Highlight a marker (used for hover effects from the marker list)
	 * @param {number} markerId - Marker ID to highlight
	 * @param {boolean} isHighlighted - Whether to highlight or unhighlight
	 * @returns {boolean} True if successful
	 */
	highlightMarker(markerId, isHighlighted) {
		return this.markerManager.highlightMarker(markerId, isHighlighted);
	}

	/**
	 * Focus on a specific marker (used when clicking from the marker list)
	 * @param {number} markerId - Marker ID to focus on
	 * @returns {boolean} True if successful
	 */
	focusOnMarker(markerId) {
		const marker = this.markerManager.getMarker(markerId);
		if (!marker) {
			this.logger.warn(`Cannot focus on marker ${markerId} - not found`);
			return false;
		}

		try {
			// Set the marker as active
			this.markerManager.setActiveMarker(markerId);

			// If marker has a dbId, select and fit to the object
			if (marker.dbId && marker.dbId !== -1) {
				this.viewer.select([marker.dbId]);
				this.viewer.fitToView([marker.dbId]);
			} else {
				// For 2D point markers without dbId, just clear selection
				this.viewer.clearSelection();
			}

			// Restore camera position if available
			if (marker.cameraData) {
				this.markerManager.cameraUtils
					.restoreCamera(marker.cameraData, {
						animate: true,
						duration: 50,
					})
					.catch((error) => {
						this.logger.warn(
							"Failed to restore camera position for marker focus",
							error
						);
						// Fallback for markers with dbId
						if (marker.dbId && marker.dbId !== -1) {
							this.viewer.fitToView([marker.dbId]);
						}
					});
			}

			// Show comment panel after a brief delay to allow camera transition
			setTimeout(() => {
				this.markerManager.showCommentPanel(marker);
			}, 200);

			return true;
		} catch (error) {
			this.logger.error(`Error focusing on marker ${markerId}`, error);
			return false;
		}
	}

	/**
	 * Export markers data for persistence
	 * @returns {Object} Export data
	 */
	exportMarkersData() {
		return this.markerManager.exportData();
	}

	/**
	 * Import markers data from saved state
	 * @param {Object} data - Import data
	 * @returns {Promise<boolean>} True if imported successfully
	 */
	async importMarkersData(data) {
		return this.markerManager.importData(data);
	}

	/**
	 * Force update all marker positions (useful for debugging)
	 */
	forceUpdateAllMarkers() {
		this.markerManager.updateAllMarkerPositions();
	}

	/**
	 * Get extension statistics and debug information
	 * @returns {Object} Debug information
	 */
	getDebugInfo() {
		return {
			extension: {
				name: EXTENSION_CONFIG.EXTENSION_NAME,
				version: EXTENSION_CONFIG.VERSION,
				loaded: !!this.viewer,
				markupMode: this.isMarkupMode,
				viewType: this.currentViewType,
				viewIndex: this.currentViewIndex,
			},
			viewer: {
				container: !!this.viewer?.container,
				model: !!this.viewer?.model,
				instanceTree: !!this.viewer?.model?.getInstanceTree(),
				is2D: this.is2DView(),
			},
			persistence: {
				viewerUrn: this.viewerUrn,
				storageKey: this.viewerUrn
					? `bimify_markups_${this.viewerUrn}_${this.currentViewType}_${this.currentViewIndex}`
					: "N/A",
				localStorageEnabled: typeof localStorage !== "undefined",
			},
			markers: this.markerManager.getStats(),
			events: this.eventHandler.getStats(),
		};
	}

	/**
	 * Update user data for new markers
	 * @param {string} userImageLink - New user image URL
	 * @param {string} userName - New user name
	 */
	updateUserData(userImageLink, userName) {
		this.userImageLink = userImageLink;
		this.userName = userName;

		// Update marker manager with new user data
		this.markerManager.updateUserData(userImageLink, userName);
	}

	/**
	 * Set viewer URN for persistence
	 * @param {string} urn - Viewer URN
	 */
	setViewerUrn(urn) {
		this.viewerUrn = urn;
	}

	/**
	 * Save current markups to storage
	 * @returns {Promise<boolean>} Promise resolving to success status
	 */
	async saveMarkups() {
		if (!this.persistenceService) {
			this.logger.warn("Cannot save markups: No persistence service available");
			return false;
		}

		if (!this.viewerUrn) {
			this.logger.error("Cannot save markups: No viewer URN specified");
			return false;
		}

		try {
			const markupData = this.markerManager.exportData();
			const result = await this.persistenceService.saveMarkups(
				this.viewerUrn,
				this.currentViewType,
				this.currentViewIndex,
				markupData
			);

			if (!result) {
				this.logger.error("Failed to save markups");
			}

			return result;
		} catch (error) {
			this.logger.error("Error saving markups", error);
			return false;
		}
	}

	/**
	 * Load markups from storage
	 * @returns {Promise<boolean>} Promise resolving to success status
	 */
	async loadMarkups() {
		if (!this.persistenceService) {
			this.logger.warn("Cannot load markups: No persistence service available");
			return false;
		}

		if (!this.viewerUrn) {
			this.logger.error("Cannot load markups: No viewer URN specified");
			return false;
		}

		try {
			const markupData = await this.persistenceService.loadMarkups(
				this.viewerUrn,
				this.currentViewType,
				this.currentViewIndex
			);

			if (markupData) {
				await this.markerManager.importData(markupData);
				return true;
			}

			this.logger.warn("No saved markups found for this view");
			return false;
		} catch (error) {
			this.logger.error("Error loading markups", error);
			return false;
		}
	}

	/**
	 * Delete saved markups from storage
	 * @returns {Promise<boolean>} Promise resolving to success status
	 */
	async deleteMarkups() {
		if (!this.persistenceService) {
			this.logger.warn(
				"Cannot delete markups: No persistence service available"
			);
			return false;
		}

		if (!this.viewerUrn) {
			this.logger.error("Cannot delete markups: No viewer URN specified");
			return false;
		}

		try {
			const result = await this.persistenceService.deleteMarkups(
				this.viewerUrn,
				this.currentViewType,
				this.currentViewIndex
			);

			if (result) {
				this.markerManager.clearAllMarkers();
			} else {
				this.logger.error("Failed to delete markups");
			}

			return result;
		} catch (error) {
			this.logger.error("Error deleting markups", error);
			return false;
		}
	}

	/**
	 * List all available markup sets for this model
	 * @returns {Promise<Array>} Promise resolving to array of available markup sets
	 */
	async listMarkupSets() {
		if (!this.persistenceService) {
			this.logger.warn(
				"Cannot list markup sets: No persistence service available"
			);
			return [];
		}

		if (!this.viewerUrn) {
			this.logger.error("Cannot list markup sets: No viewer URN specified");
			return [];
		}

		try {
			return await this.persistenceService.listMarkupSets(this.viewerUrn);
		} catch (error) {
			this.logger.error("Error listing markup sets", error);
			return [];
		}
	}

	/**
	 * Load and enable the Markups Core extension
	 */
	loadMarkupsCoreExtension() {
		try {
			this.viewer
				.loadExtension("Autodesk.Viewing.MarkupsCore")
				.then((markupsCore) => {
					this.markupsCore = markupsCore;
				})
				.catch((error) => {
					this.logger.error("Failed to load Markups Core extension", error);
				});
		} catch (error) {
			this.logger.error("Error loading Markups Core extension", error);
		}
	}

	/**
	 * Enable markup drawing mode when a marker is active
	 */
	enableMarkupDrawing() {
		if (this.markupsCore) {
			try {
				// Enable markup tools - the exact API may vary
				this.logger.info("Markup drawing tools available");
			} catch (error) {
				this.logger.error("Error enabling markup drawing", error);
			}
		} else {
			this.logger.warn("Markups Core extension not available");
		}
	}

	/**
	 * Disable markup drawing mode
	 */
	disableMarkupDrawing() {
		if (this.markupsCore) {
			try {
				this.logger.info("Markup drawing tools disabled");
			} catch (error) {
				this.logger.error("Error disabling markup drawing", error);
			}
		}
	}
}
